import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { fetchClientInstanceId } from '@/utils/clientInstance'

export interface SSOCheckResponse {
  success: boolean;
  canAuthenticateInternal: boolean;
  canAuthenticateExternal: boolean;
  externalIdPs: Array<{
      logo: string | null;
      name: string;
      description: string;
      url: string;
  }>;
  current_server_time: string;
}

export interface AcsLoginResponse {
  success: boolean
  authenticated?: boolean
  redirectUrl?: string
  current_server_time: string
}

/**
 * Composable for handling SSO/ACS (Single Sign-On/Assertion Consumer Service) login flow
 */
export function useSSO() {
  const router = useRouter()
  const authStore = useAuthStore()
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Check if the user is authenticated via SSO (getUserAuth). When VITE_APP_ENV is 'local' or
   * the app is on a local-env host (e.g. appv5-local-env.goboomtown.com), use same origin when
   * cross-origin so the request goes through the app proxy. For stage, preprod, and prod use
   * VITE_BOOMTOWN_API_HOST.
   */
  const checkSSO = async (email: string): Promise<SSOCheckResponse> => {
    try {
        console.debug('🔍 Auth Store: Checking SSO status for email:', email)

        const formData = new URLSearchParams()
        formData.append('email', email)

        const apiHost = (import.meta.env.VITE_BOOMTOWN_API_HOST as string || '').replace(/\/$/, '')
        const origin = typeof window !== 'undefined' ? window.location.origin : ''
        const appEnv = import.meta.env.VITE_APP_ENV as string | undefined
        const isLocalEnvHost = /appv5-local-env|local-env\.goboomtown\.com/i.test(origin)
        const isCrossOrigin = origin && apiHost && origin !== apiHost && !apiHost.startsWith(origin)
        const baseUrl = (appEnv === 'local' || isLocalEnvHost) && isCrossOrigin ? origin : apiHost
        const url = `${baseUrl}/admin/v4/core/?sAction=getUserAuth`

        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'x-boomtown-client-instance-id': fetchClientInstanceId(),
            'x-boomtown-csrf-token': authStore.csrfToken ?? '',
            'x-boomtown-app': 'cxme',
          },
          body: formData.toString(),
        })

        if (!response.ok) {
          const errBody = await response.text()
          console.error('❌ Auth Store: getUserAuth failed', response.status, errBody)
          throw new Error('Failed to check SSO status')
        }

        const data = (await response.json()) as SSOCheckResponse
        console.debug('✅ Auth Store: SSO check completed successfully')
        return data
    } catch (error) {
        console.error('❌ Auth Store: Failed to check SSO status:', error)
        throw new Error('Failed to check SSO status')
    }
  }

  /**
   * Complete SAML ACS preflight: POST to /api/sso/saml/<ptid>/acs so the
   * backend can set the session cookie. On stage/preprod/prod use VITE_SSO_APP_ORIGIN
   * (app.stage.goboomtown.com) since that host serves /api/sso/saml; on local use current origin.
   * @param preflight - UUID from IdP redirect query
   * @param ptid - Integration id from IdP redirect query
   */
  const submitAcsLogin = async (preflight: string, ptid: string): Promise<AcsLoginResponse> => {
    isLoading.value = true
    error.value = null

    try {
      const appEnv = import.meta.env.VITE_APP_ENV as string | undefined
      const ssoAppOrigin = (import.meta.env.VITE_SSO_APP_ORIGIN as string || '').replace(/\/$/, '')
      const origin =
        ssoAppOrigin && appEnv !== 'local'
          ? ssoAppOrigin
          : (typeof window !== 'undefined' ? window.location.origin : '')
      const acsUrl = `${origin}/api/sso/saml/${encodeURIComponent(ptid)}/acs?preflight=${encodeURIComponent(preflight)}`
      // Send preflight/ptid in body too so backend can use it if proxy strips query string
      const response = await fetch(acsUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preflight, ptid }),
      })

      if (!response.ok) {
        const errBody = await response.text()
        console.error('❌ AcsLogin: ACS request failed', response.status, errBody)
        throw new Error(`SSO could not be completed: ${response.status}`)
      }

      const data = (await response.json()) as AcsLoginResponse
      console.debug('✅ acs login: completed successfully', data)
      
      // Handle successful authentication (session cookie set for app origin)
      if (data.authenticated === true) {
        authStore.isAuthenticated = true
        // Prevent router guard from calling initialize() again after SSO login
        authStore.isInitialized = true
        // Add a small delay to ensure cookies are properly set by the browser before refreshing
        await new Promise(resolve => setTimeout(resolve, 100))
        
        // For SSO, we need to refresh CSRF token to get user data
        // Skip automatic partner/meta data loading to prevent CSRF token conflicts
        await authStore.refreshCsrfToken(true)
        
        // Clean up timeout redirect flag after successful SSO login
        sessionStorage.removeItem('redirectAfterTimeoutLogin')

        // Now manually trigger partner and meta data loading with the updated CSRF token
        try {
          const { usePartnerStore } = await import('@/stores/partner')
          const { useMetaStore } = await import('@/stores/meta')
          const { useUserStore } = await import('@/stores/user')
          
          const partnerStore = usePartnerStore()
          const metaStore = useMetaStore()
          const userStore = useUserStore()
          
          // Load partner data if we have a user object_id
          if (userStore.userData?.object_id) {
            try {
              await partnerStore.fetchCurrentPartner()
              console.log('🏢 SSO: Successfully fetched current partner data')
            } catch (partnerError) {
              console.error('🏢 SSO: Failed to fetch current partner data:', partnerError)
            }
          }
          
          // Load meta data
          try {
            await Promise.all([
              metaStore.loadMetaData(),
              metaStore.loadPartnerMetaData()
            ])
            console.log('📊 SSO: Successfully loaded meta data and partner meta data')
          } catch (metaError) {
            console.error('📊 SSO: Failed to load meta data:', metaError)
          }
        } catch (error) {
          console.error('❌ SSO: Failed to load additional data after CSRF refresh:', error)
        }

        if (authStore.redirectAfterLogin) {          
          // If redirect after login is a full URL, redirect to it directly
          if (authStore.redirectAfterLogin.startsWith('https')) {
            window.location.href = authStore.redirectAfterLogin
            authStore.redirectAfterLogin = ''
          } else {
            // Validate redirect path before navigating to prevent circular navigation
            const isValidRedirect = authStore.redirectAfterLogin !== '/login' && 
                                   !authStore.redirectAfterLogin.startsWith('/login?') &&
                                   authStore.redirectAfterLogin !== '/passwordreset' && 
                                   !authStore.redirectAfterLogin.startsWith('/passwordreset?')
            
            if (isValidRedirect) {
              try {
                await router.push(authStore.redirectAfterLogin)
                authStore.redirectAfterLogin = ''
              } catch (error) {
                // Log navigation failure as error for DataDog tracking
                console.error('❌ SSO: Redirect navigation failed:', {
                  redirectPath: authStore.redirectAfterLogin,
                  error: error instanceof Error ? error.message : 'Unknown error'
                })
                
                // Clear the failed redirect and fall back to inbox home
                authStore.redirectAfterLogin = ''
                await router.push('/inbox?view=home')
              }
            } else {
              // Invalid redirect path, clear it and go to inbox home
              console.log('🏠 useSSO: Invalid redirect path, going to inbox home instead')
              authStore.redirectAfterLogin = ''
              await router.push('/inbox?view=home')
            }
          }
        } else {
          // Default redirect to inbox home view
          console.log('🏠 useSSO: No saved redirect, going to inbox home')
          await router.push('/inbox?view=home')
        }
      }
      
      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      error.value = errorMessage
      console.error('❌ AcsLogin: ACS login error:', errorMessage)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    isLoading,
    error,
    submitAcsLogin,
    checkSSO
  }
}
